import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { WeeklyTimesheet, GustaPayrollExportData } from '../types/timesheet.ts';
import { Employee } from '../types/employee.ts';
import { FileText, Download, Calendar, User, Clock, Filter, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

interface EmployeeHoursSummary {
  employeeId: string;
  employeeName: string;
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalHours: number;
  weekCount: number;
  jobs: string[];
  averageHoursPerWeek: number;
  employee?: Employee;
}

interface WeeklyHoursSummary {
  weekEnding: Date;
  totalHours: number;
  employeeCount: number;
  jobCount: number;
  jobs: string[];
}

const TimesheetReports: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [timesheets, setTimesheets] = useState<WeeklyTimesheet[]>([]);
  const [employees, setEmployees] = useState<{ [id: string]: Employee }>({});
  const [employeeSummaries, setEmployeeSummaries] = useState<EmployeeHoursSummary[]>([]);
  const [weeklySummaries, setWeeklySummaries] = useState<WeeklyHoursSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filters
  const [dateRange, setDateRange] = useState({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate()
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJob, setSelectedJob] = useState('');
  const [reportType, setReportType] = useState<'employee' | 'weekly'>('employee');

  function getDefaultStartDate(): string {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Last 30 days
    return date.toISOString().split('T')[0];
  }

  function getDefaultEndDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  useEffect(() => {
    loadData();
  }, [dateRange]);

  useEffect(() => {
    if (timesheets.length > 0) {
      generateReports();
    }
  }, [timesheets, employees, searchTerm, selectedJob]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load timesheets within date range
      const timesheetsRef = collection(db, 'weeklyTimesheets');
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      endDate.setHours(23, 59, 59, 999);
      
      const q = query(
        timesheetsRef,
        where('weekEnding', '>=', startDate),
        where('weekEnding', '<=', endDate),
        orderBy('weekEnding', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const timesheetData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        weekEnding: doc.data().weekEnding?.toDate() || new Date(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as WeeklyTimesheet[];
      
      setTimesheets(timesheetData);
      
      // Load employees
      const employeeIds = [...new Set(
        timesheetData.flatMap(ts => 
          ts.entries.map(entry => entry.employeeId).filter(id => id)
        )
      )];
      
      if (employeeIds.length > 0) {
        const employeeData: { [id: string]: Employee } = {};
        
        for (const empId of employeeIds) {
          try {
            const employeesRef = collection(db, 'employees');
            const empQuery = query(employeesRef, where('__name__', '==', empId));
            const empSnapshot = await getDocs(empQuery);
            
            if (!empSnapshot.empty) {
              const empDoc = empSnapshot.docs[0];
              employeeData[empId] = {
                id: empDoc.id,
                ...empDoc.data(),
                createdAt: empDoc.data()?.createdAt?.toDate() || new Date(),
                updatedAt: empDoc.data()?.updatedAt?.toDate() || new Date(),
                hireDate: empDoc.data()?.hireDate?.toDate(),
                terminationDate: empDoc.data()?.terminationDate?.toDate(),
                licenseExpiration: empDoc.data()?.licenseExpiration?.toDate(),
              } as Employee;
            }
          } catch (err) {
            console.error(`Error loading employee ${empId}:`, err);
          }
        }
        
        setEmployees(employeeData);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load timesheet data');
    } finally {
      setLoading(false);
    }
  };

  const generateReports = () => {
    // Generate employee summaries
    const employeeMap = new Map<string, EmployeeHoursSummary>();
    const weeklyMap = new Map<string, WeeklyHoursSummary>();
    
    timesheets.forEach(timesheet => {
      // Apply job filter
      if (selectedJob && timesheet.jobName !== selectedJob) return;
      
      const weekKey = timesheet.weekEnding.toISOString();
      if (!weeklyMap.has(weekKey)) {
        weeklyMap.set(weekKey, {
          weekEnding: timesheet.weekEnding,
          totalHours: 0,
          employeeCount: 0,
          jobCount: 0,
          jobs: []
        });
      }
      
      const weeklySummary = weeklyMap.get(weekKey)!;
      weeklySummary.jobs.push(timesheet.jobName);
      weeklySummary.jobCount = [...new Set(weeklySummary.jobs)].length;
      
      timesheet.entries.forEach(entry => {
        // Apply employee search filter
        if (searchTerm && !entry.employeeName.toLowerCase().includes(searchTerm.toLowerCase())) {
          return;
        }
        
        const totalHours = Object.values(entry.hours).reduce((sum, hours) => sum + hours, 0);
        if (totalHours === 0) return;
        
        const regularHours = Math.min(totalHours, 40);
        const overtimeHours = Math.max(totalHours - 40, 0);
        
        const key = entry.employeeId || entry.employeeName;
        if (!employeeMap.has(key)) {
          employeeMap.set(key, {
            employeeId: entry.employeeId || '',
            employeeName: entry.employeeName,
            totalRegularHours: 0,
            totalOvertimeHours: 0,
            totalHours: 0,
            weekCount: 0,
            jobs: [],
            averageHoursPerWeek: 0,
            employee: entry.employeeId ? employees[entry.employeeId] : undefined
          });
        }
        
        const summary = employeeMap.get(key)!;
        summary.totalRegularHours += regularHours;
        summary.totalOvertimeHours += overtimeHours;
        summary.totalHours += totalHours;
        summary.jobs.push(timesheet.jobName);
        
        weeklySummary.totalHours += totalHours;
      });
      
      weeklySummary.employeeCount = timesheet.entries.filter(entry => 
        !searchTerm || entry.employeeName.toLowerCase().includes(searchTerm.toLowerCase())
      ).length;
    });
    
    // Calculate averages and deduplicate jobs
    const employeeSummaries = Array.from(employeeMap.values()).map(summary => ({
      ...summary,
      jobs: [...new Set(summary.jobs)],
      weekCount: Math.max(1, summary.jobs.length), // Approximate week count
      averageHoursPerWeek: summary.totalHours / Math.max(1, summary.jobs.length)
    }));
    
    setEmployeeSummaries(employeeSummaries.sort((a, b) => b.totalHours - a.totalHours));
    setWeeklySummaries(Array.from(weeklyMap.values()).sort((a, b) => 
      b.weekEnding.getTime() - a.weekEnding.getTime()
    ));
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    if (reportType === 'employee') {
      // Employee Hours Report
      const employeeData = employeeSummaries.map(emp => ({
        'Employee Name': emp.employeeName,
        'Employee ID': emp.employeeId,
        'Total Hours': emp.totalHours.toFixed(1),
        'Regular Hours': emp.totalRegularHours.toFixed(1),
        'Overtime Hours': emp.totalOvertimeHours.toFixed(1),
        'Average Hours/Week': emp.averageHoursPerWeek.toFixed(1),
        'Jobs Worked': emp.jobs.join(', '),
        'Week Count': emp.weekCount,
        'Hourly Rate (CEC)': emp.employee?.rates?.cec || 'N/A',
        'Hourly Rate (REL)': emp.employee?.rates?.reliable || 'N/A',
        'Hourly Rate (BTC)': emp.employee?.rates?.btc || 'N/A',
        'Status': emp.employee?.status || 'Unknown'
      }));
      
      const worksheet = XLSX.utils.json_to_sheet(employeeData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Employee Hours');
    } else {
      // Weekly Hours Report
      const weeklyData = weeklySummaries.map(week => ({
        'Week Ending': week.weekEnding.toLocaleDateString(),
        'Total Hours': week.totalHours.toFixed(1),
        'Employee Count': week.employeeCount,
        'Job Count': week.jobCount,
        'Jobs': week.jobs.join(', '),
        'Average Hours/Employee': (week.totalHours / Math.max(1, week.employeeCount)).toFixed(1)
      }));
      
      const worksheet = XLSX.utils.json_to_sheet(weeklyData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Weekly Hours');
    }
    
    const fileName = `timesheet_${reportType}_report_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    setSuccess(`${reportType === 'employee' ? 'Employee' : 'Weekly'} report exported successfully`);
  };

  const exportForGusto = () => {
    const gustoData: GustaPayrollExportData[] = [];
    
    employeeSummaries.forEach(emp => {
      if (!emp.employee) return; // Skip employees not in database
      
      // Find the most recent week ending date for this employee
      const employeeTimesheets = timesheets.filter(ts => 
        ts.entries.some(entry => entry.employeeId === emp.employeeId)
      );
      
      const latestWeekEnding = employeeTimesheets.length > 0 
        ? employeeTimesheets.sort((a, b) => b.weekEnding.getTime() - a.weekEnding.getTime())[0].weekEnding
        : new Date();
      
      const regularRate = emp.employee.rates?.cec || emp.employee.hourlyWage || 0;
      const overtimeRate = regularRate * 1.5;
      
      gustoData.push({
        employeeId: emp.employeeId,
        employeeName: emp.employeeName,
        weekEnding: latestWeekEnding,
        regularHours: emp.totalRegularHours,
        overtimeHours: emp.totalOvertimeHours,
        totalHours: emp.totalHours,
        regularPay: emp.totalRegularHours * regularRate,
        overtimePay: emp.totalOvertimeHours * overtimeRate,
        totalPay: (emp.totalRegularHours * regularRate) + (emp.totalOvertimeHours * overtimeRate)
      });
    });
    
    const gustoExport = gustoData.map(emp => ({
      'Employee ID': emp.employeeId,
      'Employee Name': emp.employeeName,
      'Week Ending': emp.weekEnding.toLocaleDateString(),
      'Regular Hours': emp.regularHours.toFixed(2),
      'Overtime Hours': emp.overtimeHours.toFixed(2),
      'Total Hours': emp.totalHours.toFixed(2),
      'Regular Pay': emp.regularPay.toFixed(2),
      'Overtime Pay': emp.overtimePay.toFixed(2),
      'Total Pay': emp.totalPay.toFixed(2)
    }));
    
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(gustoExport);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Gusto Payroll Export');
    
    const fileName = `gusto_payroll_export_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    setSuccess('Gusto payroll data exported successfully');
  };

  const uniqueJobs = [...new Set(timesheets.map(ts => ts.jobName))].sort();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Timesheet Reports</h1>
            <p className="text-gray-600 mt-1">
              Generate employee hours reports and export payroll data
            </p>
          </div>
          <button
            onClick={() => navigate('/timesheets')}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Back to Timesheets
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
          <button onClick={() => setError(null)} className="ml-auto">×</button>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto">×</button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Filters</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report Type
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value as 'employee' | 'weekly')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="employee">Employee Hours</option>
              <option value="weekly">Weekly Summary</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Filter
            </label>
            <select
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Jobs</option>
              {uniqueJobs.map(job => (
                <option key={job} value={job}>{job}</option>
              ))}
            </select>
          </div>
        </div>
        
        {reportType === 'employee' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search by employee name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full md:w-1/3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Export Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Export Options</h2>
        
        <div className="flex flex-wrap gap-3">
          <button
            onClick={exportToExcel}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </button>
          
          <button
            onClick={exportForGusto}
            className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Export for Gusto
          </button>
        </div>
      </div>

      {/* Report Display */}
      {reportType === 'employee' ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Employee Hours Report ({employeeSummaries.length} employees)
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Regular</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overtime</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg/Week</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jobs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rates</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employeeSummaries.map((emp, index) => (
                  <tr key={emp.employeeId || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{emp.employeeName}</div>
                      <div className="text-sm text-gray-500">
                        {emp.employee?.status || 'Unknown'} 
                        {!emp.employee && <span className="text-yellow-600"> (Not in DB)</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{emp.totalHours.toFixed(1)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {emp.totalRegularHours.toFixed(1)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {emp.totalOvertimeHours > 0 ? (
                        <span className="text-orange-600 font-medium">{emp.totalOvertimeHours.toFixed(1)}</span>
                      ) : (
                        '0'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {emp.averageHoursPerWeek.toFixed(1)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="max-w-xs truncate" title={emp.jobs.join(', ')}>
                        {emp.jobs.slice(0, 2).join(', ')}
                        {emp.jobs.length > 2 && ` +${emp.jobs.length - 2} more`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                      {emp.employee ? (
                        <div>
                          <div>CEC: ${emp.employee.rates?.cec || 'N/A'}</div>
                          {emp.employee.rates?.reliable && <div>REL: ${emp.employee.rates.reliable}</div>}
                          {emp.employee.rates?.btc && <div>BTC: ${emp.employee.rates.btc}</div>}
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Weekly Hours Summary ({weeklySummaries.length} weeks)
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Week Ending</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employees</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jobs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Hours/Employee</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {weeklySummaries.map((week, index) => (
                  <tr key={week.weekEnding.toISOString()} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {week.weekEnding.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{week.totalHours.toFixed(1)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {week.employeeCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {week.jobCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(week.totalHours / Math.max(1, week.employeeCount)).toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimesheetReports;